'use client'

import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { List, X } from '@phosphor-icons/react'
import { GravityLogo } from './GravityLogo'

/* ── Innovaccer icon mark ── */
function InnovaccerIcon({ height = 36 }: { height?: number }) {
  return (
    <svg style={{ height, width: 'auto', display: 'block' }} viewBox="0 0 28 34" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M8.36276 18.7476L0 33.1253L16.6313 33.1768L8.36276 18.7476Z" fill="#000a1b"/>
      <path d="M8.3798 14.4292L16.7426 0.0514104L0.111289 0L8.3798 14.4292Z" fill="#000a1b"/>
      <path d="M10.2478 15.5089L26.879 15.5689L18.6105 1.13966L10.2478 15.5089Z" fill="#000a1b"/>
      <path d="M10.2394 17.6765L18.5079 32.1057L26.8707 17.7279L10.2394 17.6765Z" fill="#000a1b"/>
    </svg>
  )
}

/* Figma: Rectangle 7361 — solid right-pointing triangle, 8.12×12px
   Used in both desktop cards and mobile sub-items.
   `open` rotates it 90° downward for the PRODUCTS expander. */
function SolidArrow({ open = false }: { open?: boolean; size?: number }) {
  return (
    <svg
      viewBox="0 0 8.12132 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{
        width: 13.5355,
        height: 11,
        display: 'block',
        flexShrink: 0,
        transform: open ? 'rotate(90deg)' : 'none',
        transition: 'transform 200ms',
      }}
    >
      <path d="M0 0L8.12132 6L5.05762e-07 12L0 0Z" fill="#000A1B" />
    </svg>
  )
}

/* Alias for desktop dropdown cards (no rotation) */
function CardArrow() {
  return (
    <svg
      className="gravity-dropdown__card-arrow"
      viewBox="0 0 8.12132 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M0 0L8.12132 6L5.05762e-07 12L0 0Z" fill="#000A1B" />
    </svg>
  )
}

interface StudioCard  { title: string; description: string; href: string; badge?: string }
interface ProductCard {
  title: string; description: string; href: string
  previewSrc: string
  hoverPreviewSrc?: string  // Figma Variant2 image — more vivid/coloured
  badge?: string
}

const STUDIO_CARDS: StudioCard[] = [
  { title: 'AI Studio',                 description: 'Build, deploy, and scale healthcare-specific AI agents and workflows', href: 'https://innovaccer.com/gravity/ai-studio' },
  { title: 'Developer Studio',          description: 'Design, test, and launch production ready applications',              href: 'https://innovaccer.com/gravity/dev-studio' },
  { title: 'Data and Analytics Studio', description: 'Manage, analyse, and govern healthcare data with ease',                href: '#', badge: 'Coming Soon' },
]

const PRODUCT_CARDS: ProductCard[] = [
  {
    title: 'Gravity Shield',
    description: 'Purpose built for healthcare. Secure by design.',
    href: 'https://innovaccer.com/gravity/shield',
    previewSrc:      '/media/gravity/dropdown/preview-shield.png',
    hoverPreviewSrc: '/media/gravity/dropdown/preview-shield-hover.png',
  },
  {
    title: 'Gravity Search',
    description: 'Turn healthcare data into Real-Time Insights.',
    href: 'https://innovaccer.com/gravity/search',
    previewSrc:      '/media/gravity/dropdown/preview-search.png',
    hoverPreviewSrc: '/media/gravity/dropdown/preview-search-hover.png',
    badge: 'Coming Soon',
  },
  {
    title: 'Gravity AI agents',
    description: 'Build, govern, and scale AI across the enterprise',
    href: 'https://get-gravity.ai/agents',
    previewSrc:      '/media/gravity/dropdown/preview-ai-agents.png',
    hoverPreviewSrc: '/media/gravity/dropdown/preview-ai-agents-hover.png',
  },
]

const NAV_ITEMS = [
  { label: '9.8 ACADEMY', href: 'https://innovaccer.com/gravity-training' },
  { label: 'PARTNER',     href: 'https://innovaccer.com/gravity/partner' },
  { label: 'RESOURCES',   href: 'https://innovaccer.com/gravity/resources' },
]

function CtaText({ label }: { label: string }) {
  return (
    <span className="gravity-nav__cta-text">
      <span className="btn-primary-text">{label}</span>
      <span className="btn-hover-text">{label}</span>
    </span>
  )
}

function DropdownPanel({ onMouseEnter, onMouseLeave }: { onMouseEnter: () => void; onMouseLeave: () => void }) {
  return (
    <div
      className="gravity-nav__dropdown-panel"
      role="menu"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Section 1: Gravity Studios — chalk cards, no preview image */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <p className="gravity-dropdown__section-label">Gravity Studios</p>
        <div className="gravity-dropdown__row">
          {STUDIO_CARDS.map((card) => (
            <a key={card.title} href={card.href} className="gravity-dropdown__card gravity-dropdown__card--chalk" role="menuitem">
              <div className="gravity-dropdown__card-header">
                <span className="gravity-dropdown__card-title">{card.title}</span>
                {card.badge && <span className="gravity-dropdown__card-badge">{card.badge}</span>}
              </div>
              <div className="gravity-dropdown__card-desc-row">
                <span className="gravity-dropdown__card-desc">{card.description}</span>
                <CardArrow />
              </div>
            </a>
          ))}
        </div>
      </div>

      <div className="gravity-dropdown__divider" />

      {/* Section 2: Gravity Products — white cards with dark preview image */}
      <div className="gravity-dropdown__row">
        {PRODUCT_CARDS.map((card) => (
          <a key={card.title} href={card.href} className="gravity-dropdown__card gravity-dropdown__card--white" role="menuitem">
            <div className="gravity-dropdown__card-text">
              <div className="gravity-dropdown__card-header">
                <span className="gravity-dropdown__card-title">{card.title}</span>
                {card.badge && <span className="gravity-dropdown__card-badge">{card.badge}</span>}
              </div>
              <div className="gravity-dropdown__card-desc-row">
                <span className="gravity-dropdown__card-desc">{card.description}</span>
                <CardArrow />
              </div>
            </div>
            {/* Crossfade default → hover image (Figma Variant2 where available) */}
            <div className="gravity-dropdown__card-preview-wrap">
              <img src={card.previewSrc} alt="" className="gravity-dropdown__card-preview-default" />
              {card.hoverPreviewSrc && (
                <img src={card.hoverPreviewSrc} alt="" className="gravity-dropdown__card-preview-hover" />
              )}
            </div>
          </a>
        ))}
      </div>
    </div>
  )
}

export function GravityNavBar() {
  const [mobileOpen, setMobileOpen]     = useState(false)
  const [productsOpen, setProductsOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [mounted, setMounted]           = useState(false)
  const [navBottom, setNavBottom]       = useState(0)
  const headerRef  = useRef<HTMLElement>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => { setMounted(true) }, [])

  /* Measure nav bottom edge whenever dropdown opens */
  useEffect(() => {
    if (dropdownOpen && headerRef.current) {
      setNavBottom(headerRef.current.getBoundingClientRect().bottom)
    }
  }, [dropdownOpen])

  const openDropdown = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    setDropdownOpen(true)
  }
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setDropdownOpen(false), 150)
  }

  return (
    <header className="gravity-nav" ref={headerRef}>
      <div className="gravity-nav__card">
        {/* Left: brand + nav */}
        <div className="gravity-nav__left">
          <div className="gravity-nav__brand">
            <a href="https://innovaccer.com" className="gravity-nav__logo gravity-nav__logo--parent" aria-label="Innovaccer">
              <InnovaccerIcon height={32} />
            </a>
            <a href="https://get-gravity.ai/" className="gravity-nav__logo" aria-label="Gravity by Innovaccer">
              <GravityLogo height={40} />
            </a>
          </div>

          <nav className="gravity-nav__menu" aria-label="Primary">
            <button
              type="button"
              className="gravity-nav__dropdown-trigger"
              aria-haspopup="true"
              aria-expanded={dropdownOpen}
              onMouseEnter={openDropdown}
              onMouseLeave={scheduleClose}
              onFocus={openDropdown}
              onBlur={scheduleClose}
            >
              PRODUCTS
            </button>
            {NAV_ITEMS.map((item) => (
              <a key={item.label} href={item.href} className="gravity-nav__item">{item.label}</a>
            ))}
          </nav>
        </div>

        {/* Right: CTAs + hamburger */}
        <div className="gravity-nav__right">
          <a href="https://get-gravity.ai/gravity/become-a-partner" className="gravity-nav__cta gravity-nav__cta--filled">
            <CtaText label="BECOME A PARTNER" />
          </a>
          <a href="https://get-gravity.ai/gravity/request-a-demo" className="gravity-nav__cta gravity-nav__cta--outline">
            <CtaText label="REQUEST A DEMO" />
          </a>
          <button
            type="button"
            className="gravity-nav__hamburger"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {/* Dropdown panel — direct child of <header> so position:absolute resolves
          to the sticky nav's full width, centering correctly */}
      {dropdownOpen && (
        <DropdownPanel onMouseEnter={openDropdown} onMouseLeave={scheduleClose} />
      )}

      {/* Backdrop blur — portalled to body, same pattern as FlowNavBar / NavBarMain */}
      {mounted && dropdownOpen && createPortal(
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            inset: 0,
            top: navBottom,
            zIndex: 999,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            background: 'rgba(0, 0, 0, 0.15)',
            pointerEvents: 'auto',
          }}
          onClick={() => setDropdownOpen(false)}
        />,
        document.body
      )}

      {/* Mobile drawer */}
      <div className={`gravity-nav__mobile${mobileOpen ? ' is-open' : ''}`}>
        <nav className="gravity-nav__mobile-menu" aria-label="Mobile primary">

          <a href="https://innovaccer.com/gravity/gravity-training" className="gravity-nav__mobile-nav-item" onClick={() => setMobileOpen(false)}>
            <span className="gravity-nav__mobile-nav-item__label">9.8 ACADEMY</span>
          </a>

          {/* PRODUCTS — solid triangle arrow rotates 90° down when open */}
          <button
            type="button"
            className="gravity-nav__mobile-nav-item"
            onClick={() => setProductsOpen((o) => !o)}
            aria-expanded={productsOpen}
          >
            <span className="gravity-nav__mobile-nav-item__label">PRODUCTS</span>
            <span className="gravity-nav__mobile-nav-item__arrow" aria-hidden="true">
              <SolidArrow open={productsOpen} size={20} />
            </span>
          </button>

          {/* Sub-items: limestone cards with title + description */}
          <div className={`gravity-nav__mobile-products${productsOpen ? ' is-open' : ''}`}>
            {[...STUDIO_CARDS, ...PRODUCT_CARDS].map((card) => (
              <a key={card.title} href={card.href} className="gravity-nav__mobile-product-item" onClick={() => setMobileOpen(false)}>
                <div className="gravity-nav__mobile-product-item__content">
                  <span className="gravity-nav__mobile-product-item__name">{card.title}</span>
                  <span className="gravity-nav__mobile-product-item__desc">{card.description}</span>
                </div>
                <SolidArrow size={20} />
              </a>
            ))}
          </div>

          <a href="https://innovaccer.com/gravity/partner" className="gravity-nav__mobile-nav-item" onClick={() => setMobileOpen(false)}>
            <span className="gravity-nav__mobile-nav-item__label">PARTNER</span>
          </a>

          <a href="https://get-gravity.ai/gravity/resources" className="gravity-nav__mobile-nav-item" onClick={() => setMobileOpen(false)}>
            <span className="gravity-nav__mobile-nav-item__label">RESOURCES</span>
          </a>
        </nav>

        <div className="gravity-nav__mobile-ctas">
          <a href="https://get-gravity.ai/gravity/become-a-partner" className="gravity-nav__cta gravity-nav__cta--outline gravity-nav__cta--block" onClick={() => setMobileOpen(false)}>
            <CtaText label="BECOME A PARTNER" />
          </a>
          <a href="https://get-gravity.ai/gravity/request-a-demo" className="gravity-nav__cta gravity-nav__cta--filled gravity-nav__cta--block" onClick={() => setMobileOpen(false)}>
            <CtaText label="REQUEST A DEMO" />
          </a>
        </div>
      </div>
    </header>
  )
}

export default GravityNavBar
